import { useState } from "react";
import { Routes, Route, NavLink, Navigate, useNavigate } from "react-router-dom";
import { Flame, LayoutDashboard, ShoppingCart, Package, FileText, ClipboardCheck, Users, LogOut, Store, Inbox, MessageSquare, Menu, X, Receipt, Settings } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import Overview from "@/pages/portal/Overview";
import Shop from "@/pages/portal/Shop";
import Orders from "@/pages/portal/Orders";
import Invoices from "@/pages/portal/Invoices";
import Inspections from "@/pages/portal/Inspections";
import AdminProducts from "@/pages/portal/AdminProducts";
import AdminUsers from "@/pages/portal/AdminUsers";
import Leads from "@/pages/portal/Leads";
import Quotations from "@/pages/portal/Quotations";
import Billing from "@/pages/portal/Billing";
import CompanySettings from "@/pages/portal/CompanySettings";

const NAV = {
  admin: [
    ["", "Overview", LayoutDashboard],
    ["leads", "Leads", MessageSquare],
    ["products", "Products", Package],
    ["orders", "Enquiries", Inbox],
    ["quotations", "Quotations", FileText],
    ["billing", "Billing & Payments", Receipt],
    ["invoices", "Quick Invoice", FileText],
    ["inspections", "Inspections", ClipboardCheck],
    ["users", "Users", Users],
    ["company-settings", "Company Settings", Settings],
  ],
  client: [
    ["", "Overview", LayoutDashboard],
    ["shop", "Request a Quote", Store],
    ["orders", "My Enquiries", Inbox],
    ["quotations", "Quotations", FileText],
    ["billing", "My Invoices", Receipt],
    ["invoices", "Quick Invoice", FileText],
    ["inspections", "My Inspections", ClipboardCheck],
  ],
  technician: [
    ["", "Overview", LayoutDashboard],
    ["inspections", "Inspections", ClipboardCheck],
  ],
};

export default function Portal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const nav = NAV[user.role] || [];
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-30 h-14 flex items-center justify-between px-4 bg-secondary text-white">
        <div className="flex items-center gap-2">
          <Flame className="text-primary" size={20} />
          <span className="font-heading font-black tracking-tight text-sm">SECUREFLAME</span>
        </div>
        <button onClick={() => setSidebarOpen(true)} className="p-2 -mr-2 text-white" data-testid="portal-menu-toggle" aria-label="Open menu">
          <Menu size={22} />
        </button>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setSidebarOpen(false)} data-testid="portal-sidebar-overlay" />
      )}

      {/* Sidebar */}
      <aside className={`w-60 shrink-0 bg-secondary text-white flex flex-col fixed h-screen z-50 transition-transform duration-200 lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="h-16 flex items-center justify-between gap-2 px-6 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Flame className="text-primary" size={22} />
            <span className="font-heading font-black tracking-tight">SECUREFLAME</span>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden p-1 text-slate-300 hover:text-white" data-testid="portal-sidebar-close" aria-label="Close menu">
            <X size={20} />
          </button>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {nav.map(([path, label, Icon]) => (
            <NavLink key={path} to={`/portal/${path}`} end={path === ""} onClick={() => setSidebarOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 rounded-sm text-sm font-medium transition-colors ${isActive ? "bg-primary text-white" : "text-slate-300 hover:bg-white/10 hover:text-white"}`}
              data-testid={`nav-${path || "overview"}`}>
              <Icon size={18} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <p className="text-sm font-semibold truncate">{user.name}</p>
          <p className="text-xs text-slate-400 capitalize">{user.role}</p>
          <Button onClick={() => { logout(); navigate("/"); }} variant="ghost" size="sm" className="mt-3 w-full justify-start gap-2 text-slate-300 hover:text-white hover:bg-white/10 rounded-sm" data-testid="logout-btn">
            <LogOut size={16} /> Sign out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 lg:ml-60 min-h-screen pt-14 lg:pt-0">
        <Routes>
          <Route path="" element={<Overview />} />
          {user.role === "admin" && <Route path="leads" element={<Leads />} />}
          <Route path="shop" element={<Shop />} />
          <Route path="orders" element={<Orders />} />
          <Route path="quotations" element={<Quotations />} />
          <Route path="billing" element={<Billing />} />
          <Route path="invoices" element={<Invoices />} />
          <Route path="inspections" element={<Inspections />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="users" element={<AdminUsers />} />
          {user.role === "admin" && <Route path="company-settings" element={<CompanySettings />} />}
          <Route path="*" element={<Navigate to="/portal" replace />} />
        </Routes>
      </main>
    </div>
  );
}

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-1">{subtitle}</p>
        <h1 className="font-heading font-bold text-3xl text-secondary tracking-tight">{title}</h1>
      </div>
      {children}
    </div>
  );
}
